import { useState, useEffect, useCallback } from 'react';
import { Plus, Download, Save, FileJson, AlertCircle, CheckCircle, ArrowLeft, DollarSign, Keyboard } from 'lucide-react';
import {
  InvoiceHeaderSection,
  InvoicePartiesSection,
  InvoiceLineItemSection,
  InvoiceSummarySection,
} from './invoice-sections';
import { KeyboardShortcuts } from './KeyboardShortcuts';
import type {
  InvoiceFormData,
  InvoiceLineItem,
  PartyEntry,
  AllowanceCharge,
  TaxDetail,
} from '../types/invoice';
import type { TradingPartnerWithTemplate, TemplateConfig } from '../types/tradingPartner';
import { validateInvoice, getErrorsAsRecord, calculateLineItemTotal, calculateSummary } from '../utils/invoiceValidation';
import { applyInvoiceTemplate, createInvoiceLineItem } from '../utils/invoiceTemplateApplicator';
import { overlayCustomerDefaults, getCachedCustomerDefaults } from '../utils/customerDefaults';
import { exportToJSON, downloadJSON } from '../utils/export';
import { useKeyboardShortcut } from '../utils/keyboard';
import {
  fetchInvoiceDocumentById,
  updateInvoiceDocument,
} from '../utils/documents';
import { eventBus } from '../utils/eventBus';
import { generateReferenceNumber } from '../utils/generators';
import { isSaveBlockedByExpiry, EXPIRED_SAVE_MSG } from './sessionSaveGuard';

const initialFormData: InvoiceFormData = {
  purposeCode: '00',
  invoiceNumber: '',
  invoiceDate: '',
  invoiceType: 'IN',
  purchaseOrderNumber: '',
  purchaseOrderDate: '',
  currencyCode: 'USD',
  // WDEV-142 (Natalie): "WE DO NOT NEED PAYMENT TERMS OR PAYMENT TERM
  // DESCRIPTIONS". No fabricated default (EDI rule #5) — a template that wants
  // Net 30 declares it in defaults.header.paymentTerms; the applicator merges it.
  paymentTerms: '',
  paymentTermsDescription: '',
  dates: [
    { qualifier: '011', date: '' },
  ],
  references: [
    { qualifier: 'PK', id: '' },
  ],
  names: [
    { type: 'BT', name: '', qualifier: '92', id: '' },
    { type: 'SE', name: '', qualifier: '92', id: '' },
    { type: 'SF', name: '', qualifier: '92', id: '', address1: '', city: '', state: '', postalCode: '', country: 'US' },
    { type: 'ST', name: '', qualifier: '92', id: '', address1: '', city: '', state: '', postalCode: '', country: 'US' },
  ],
  details: [],
  headerAllowancesCharges: [],
  headerTaxes: [],
  summary: {
    totalLineItems: 0,
    subtotal: '0.00',
    totalTax: '0.00',
    totalAllowances: '0.00',
    totalCharges: '0.00',
    invoiceTotal: '0.00',
  },
};

interface InvoiceFormProps {
  documentId?: string;
  tradingPartner?: TradingPartnerWithTemplate;
  externalData?: Partial<InvoiceFormData>;
  /** When true the form was opened via Mode B (full document re-open). Shows "Edit Invoice (Copy)" in the title. */
  isCopy?: boolean;
  onBack: () => void;
  sessionExpired?: boolean;
}

export function InvoiceForm({ documentId, tradingPartner, externalData, isCopy = false, onBack, sessionExpired = false }: InvoiceFormProps) {
  const [formData, setFormData] = useState<InvoiceFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showValidation, setShowValidation] = useState(false);
  const [jsonPreview, setJsonPreview] = useState('');
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveErrorDetail, setSaveErrorDetail] = useState<string | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [savedFormSnapshot, setSavedFormSnapshot] = useState<string>(JSON.stringify(initialFormData));

  useEffect(() => {
    if (documentId) {
      loadDocument();
    } else {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');

      let newFormData: InvoiceFormData = {
        ...initialFormData,
        invoiceDate: dateStr,
        purchaseOrderDate: dateStr,
      };

      // WDEV-51: apply the trading-partner template to seed header defaults,
      // parties, dates, references, and header allowances/charges.
      let overlaidConfig: TemplateConfig | undefined;
      if (tradingPartner?.template?.template_config) {
        overlaidConfig = overlayCustomerDefaults(
          tradingPartner.template.template_config,
          getCachedCustomerDefaults(),
          'invoice',
          tradingPartner.id,
        );
        newFormData = applyInvoiceTemplate(newFormData, overlaidConfig);
      }

      // WDEV-142 (Natalie): "CAN WE HAVE THIS AUTOMATICALLY HAVE ONE LINE BE
      // AVAILABLE?" — seed a single editable line item (with the template's UOM
      // default) so the operator starts with one row instead of an empty table.
      if (newFormData.details.length === 0) {
        newFormData = { ...newFormData, details: [createInvoiceLineItem(1, overlaidConfig)] };
      }

      if (externalData) {
        newFormData = { ...newFormData, ...externalData };
      }

      setFormData(newFormData);
      setSavedFormSnapshot(JSON.stringify(newFormData));
    }
  }, [documentId, tradingPartner, externalData]);

  const loadDocument = async () => {
    if (!documentId) return;

    setLoading(true);
    try {
      const doc = await fetchInvoiceDocumentById(documentId);
      if (doc) {
        setFormData(doc.data);
        setSavedFormSnapshot(JSON.stringify(doc.data));
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Failed to load document:', error);
      alert('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentSnapshot = JSON.stringify(formData);
    setHasUnsavedChanges(currentSnapshot !== savedFormSnapshot);
  }, [formData, savedFormSnapshot]);

  const recalculateSummary = useCallback(() => {
    setFormData((prev) => {
      const updatedData = { ...prev };
      calculateSummary(updatedData);
      return updatedData;
    });
  }, []);

  const handleDateChange = (qualifier: string, date: string) => {
    setFormData((prev) => {
      const dates = [...prev.dates];
      const idx = dates.findIndex(d => d.qualifier === qualifier);
      if (idx >= 0) {
        dates[idx] = { ...dates[idx], date };
      } else {
        dates.push({ qualifier, date });
      }
      return { ...prev, dates };
    });
    // Clear the validation error for this date qualifier
    const errorKey = `dates_${qualifier}`;
    if (errors[errorKey]) {
      setErrors((prev) => { const n = { ...prev }; delete n[errorKey]; return n; });
    }
  };

  const handleReferenceChange = (qualifier: string, id: string) => {
    setFormData((prev) => {
      const references = [...prev.references];
      const idx = references.findIndex(r => r.qualifier === qualifier);
      if (idx >= 0) {
        references[idx] = { ...references[idx], id };
      } else {
        references.push({ qualifier, id });
      }
      return { ...prev, references };
    });
    const errorKey = `references_${qualifier}`;
    if (errors[errorKey]) {
      setErrors((prev) => { const n = { ...prev }; delete n[errorKey]; return n; });
    }
  };

  const handleHeaderChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddParty = useCallback(() => {
    const newParty: PartyEntry = {
      type: 'BT',
      name: '',
      qualifier: '92',
      id: '',
    };
    setFormData((prev) => ({
      ...prev,
      names: [...prev.names, newParty],
    }));
  }, []);

  const handleRemoveParty = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      names: prev.names.filter((_, i) => i !== index),
    }));
  };

  const handlePartyChange = (index: number, field: keyof PartyEntry, value: string) => {
    setFormData((prev) => {
      const newParties = [...prev.names];
      newParties[index] = { ...newParties[index], [field]: value };
      return { ...prev, names: newParties };
    });
  };

  const handleAddLineItem = useCallback(() => {
    const config = tradingPartner?.template?.template_config
      ? overlayCustomerDefaults(
          tradingPartner.template.template_config,
          getCachedCustomerDefaults(),
          'invoice',
          tradingPartner.id,
        )
      : undefined;
    setFormData((prev) => ({
      ...prev,
      details: [...prev.details, createInvoiceLineItem(prev.details.length + 1, config)],
    }));
  }, [tradingPartner]);

  const handleRemoveLineItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }));
    setTimeout(recalculateSummary, 0);
  };

  const handleLineItemChange = (index: number, field: keyof InvoiceLineItem, value: any) => {
    setFormData((prev) => {
      const newLineItems = [...prev.details];
      newLineItems[index] = { ...newLineItems[index], [field]: value };
      return { ...prev, details: newLineItems };
    });
  };

  const handleCalculateLineItem = (index: number) => {
    setFormData((prev) => {
      const newLineItems = [...prev.details];
      const item = newLineItems[index];
      item.extendedAmount = calculateLineItemTotal(item.quantity, item.unitPrice);
      return { ...prev, details: newLineItems };
    });
    setTimeout(recalculateSummary, 0);
  };

  const handleAddHeaderAllowanceCharge = () => {
    const newAC: AllowanceCharge = {
      indicator: 'C',
      code: 'G830',
      amount: '',
    };
    setFormData((prev) => ({
      ...prev,
      headerAllowancesCharges: [...prev.headerAllowancesCharges, newAC],
    }));
  };

  const handleRemoveHeaderAllowanceCharge = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      headerAllowancesCharges: prev.headerAllowancesCharges.filter((_, i) => i !== index),
    }));
    setTimeout(recalculateSummary, 0);
  };

  const handleHeaderAllowanceChargeChange = (index: number, field: keyof AllowanceCharge, value: string) => {
    setFormData((prev) => {
      const updated = [...prev.headerAllowancesCharges];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, headerAllowancesCharges: updated };
    });
    setTimeout(recalculateSummary, 0);
  };

  const handleAddHeaderTax = () => {
    const newTax: TaxDetail = {
      type: 'ST',
      amount: '',
      percent: '',
    };
    setFormData((prev) => ({
      ...prev,
      headerTaxes: [...prev.headerTaxes, newTax],
    }));
  };

  const handleRemoveHeaderTax = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      headerTaxes: prev.headerTaxes.filter((_, i) => i !== index),
    }));
    setTimeout(recalculateSummary, 0);
  };

  const handleHeaderTaxChange = (index: number, field: keyof TaxDetail, value: string) => {
    setFormData((prev) => {
      const updated = [...prev.headerTaxes];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, headerTaxes: updated };
    });
    setTimeout(recalculateSummary, 0);
  };

  const handleValidate = useCallback(() => {
    const validationErrors = validateInvoice(formData);
    const errorsRecord = getErrorsAsRecord(validationErrors);
    setErrors(errorsRecord);
    setShowValidation(true);

    const isValid = validationErrors.length === 0;

    eventBus.emit('document:validated', {
      type: 'invoice',
      documentId,
      isValid,
      errors: errorsRecord,
    });

    if (isValid) {
      // In ASNForm we don't alert here, but logic is fine
    }
    return isValid;
  }, [formData, documentId]);

  const handlePreviewJSON = () => {
    const json = exportToJSON(formData);
    setJsonPreview(json);
    setShowJsonPreview(true);
  };

  const handleExportJSON = useCallback(() => {
    const isValid = handleValidate();
    if (!isValid) return;

    const filename = `invoice_${formData.invoiceNumber || 'draft'}_${Date.now()}.json`;
    downloadJSON(formData, filename);

    eventBus.emit('document:exported', {
      type: 'invoice',
      documentId,
      data: formData,
      format: 'json',
    });
  }, [formData, documentId, handleValidate]);

  const handleSave = useCallback(async () => {
    if (isSaveBlockedByExpiry()) {
      setSaveErrorDetail(EXPIRED_SAVE_MSG);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
      return;
    }
    setSaveErrorDetail(null);
    let dataToSave = { ...formData };

    if (!dataToSave.invoiceNumber || dataToSave.invoiceNumber.trim() === '') {
      const newId = generateReferenceNumber('INV');
      dataToSave = { ...dataToSave, invoiceNumber: newId };
      setFormData(dataToSave);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.invoiceNumber;
        return newErrors;
      });
    }

    setSaveStatus('saving');
    try {
      if (!documentId) {
        console.error('Cannot save: no documentId. Document should have been created on partner selection.');
        setSaveStatus('error');
        setSaveErrorDetail(null);
        setTimeout(() => setSaveStatus('idle'), 3000);
        return;
      }

      await updateInvoiceDocument(documentId, dataToSave, dataToSave.status as any);
      eventBus.emit('document:status-updated', {
        type: 'invoice',
        documentId,
        status: 'updated',
      });

      setSavedFormSnapshot(JSON.stringify(dataToSave));
      setHasUnsavedChanges(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      setSaveStatus('error');
      setSaveErrorDetail(null);
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [documentId, formData]);

  const handleSaveAndExit = useCallback(async () => {
    await handleSave();
    setTimeout(() => onBack(), 500);
  }, [handleSave, onBack]);

  const handleBackClick = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowExitConfirm(true);
    } else {
      onBack();
    }
  }, [hasUnsavedChanges, onBack]);

  const handleExitWithoutSaving = useCallback(() => {
    setShowExitConfirm(false);
    onBack();
  }, [onBack]);

  const handleSaveAndExitConfirm = useCallback(async () => {
    setShowExitConfirm(false);
    await handleSave();
    setTimeout(() => onBack(), 500);
  }, [handleSave, onBack]);

  useKeyboardShortcut('s', handleSave);
  useKeyboardShortcut('d', handleValidate);
  useKeyboardShortcut('e', handleExportJSON);
  // useKeyboardShortcut('o', handleAddOrder); // No orders in invoice
  useKeyboardShortcut('l', handleAddLineItem, [handleAddLineItem]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowShortcuts(true);
      }
      if (e.key === 'Escape') {
        setShowJsonPreview(false);
        setShowShortcuts(false);
        setShowExitConfirm(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if ((window as any).Cypress) {
        return;
      }

      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const validationErrorCount = Object.keys(errors).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading document...</div>
      </div>
    );
  }

  // Validate Trading Partner is present
  if (!tradingPartner && !documentId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="text-red-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Missing Trading Partner</h3>
            </div>
          </div>
          <p className="text-gray-600 mb-6">
            A Trading Partner is required to create a document. Please go back and select a Trading Partner first.
          </p>
          <button
            onClick={onBack}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto px-4 py-6">
        <div className="mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <button onClick={handleBackClick} className="hover:text-gray-900 flex items-center gap-1">
              <ArrowLeft size={16} />
              Back to Documents
            </button>
            <span>/</span>
            <span>
              {documentId
                ? isCopy
                  ? 'Edit Invoice (Copy)'
                  : 'Edit Invoice'
                : 'New Invoice'}
            </span>
            {hasUnsavedChanges && (
              <span className="text-orange-600 text-xs font-medium">• Unsaved Changes</span>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign size={32} />
            {documentId
              ? isCopy
                ? `Edit Invoice (Copy): ${formData.invoiceNumber}`
                : `Edit Invoice: ${formData.invoiceNumber}`
              : 'Create New Invoice'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Caterpillar EDI X12 Compliant • Press <kbd className="kbd">?</kbd> for shortcuts
          </p>
        </div>

        {showValidation && validationErrorCount > 0 && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <h3 className="font-medium text-red-900 text-sm">Validation Errors ({validationErrorCount})</h3>
              <ul className="text-xs text-red-700 mt-1 list-disc list-inside space-y-0.5">
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {showValidation && validationErrorCount === 0 && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3 flex items-start gap-2">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <h3 className="font-medium text-green-900 text-sm">Ready to Export</h3>
              <p className="text-xs text-green-700 mt-0.5">All fields valid</p>
            </div>
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <h3 className="font-medium text-red-900 text-sm">Save Failed</h3>
              <p className="text-xs text-red-700 mt-0.5">{saveErrorDetail ?? 'Failed to save document. Please try again.'}</p>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            onClick={() => handleValidate()}
            className="btn-primary px-3 py-1.5 text-sm"
            title="Ctrl+D"
          >
            <CheckCircle size={16} className="inline mr-1.5" />
            Validate
          </button>
          <button
            onClick={handleSave}
            className="btn-primary px-3 py-1.5 text-sm"
            title="Ctrl+S"
            disabled={saveStatus === 'saving' || sessionExpired}
          >
            <Save size={16} className="inline mr-1.5" />
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : 'Save'}
          </button>
          <button
            onClick={handleSaveAndExit}
            className="btn-secondary px-3 py-1.5 text-sm"
            disabled={saveStatus === 'saving' || sessionExpired}
          >
            <Save size={16} className="inline mr-1.5" />
            Save & Exit
          </button>
          
          
          <button
            onClick={() => setShowShortcuts(true)}
            className="btn-secondary px-3 py-1.5 text-sm ml-auto"
            title="?"
          >
            <Keyboard size={16} className="inline mr-1.5" />
            Shortcuts
          </button>
        </div>

        <InvoiceHeaderSection
          formData={formData}
          errors={errors}
          onChange={handleHeaderChange}
          onDateChange={handleDateChange}
          onReferenceChange={handleReferenceChange}
        />

        <InvoicePartiesSection
          parties={formData.names}
          errors={errors}
          onChange={handlePartyChange}
          onAdd={handleAddParty}
          onRemove={handleRemoveParty}
        />

        <div className="card-compact p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
            <button
              onClick={handleAddLineItem}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Plus size={16} />
              Add Line Item
            </button>
          </div>

          {formData.details.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              No line items added. Click "Add Line Item" to start adding items to this invoice.
            </p>
          )}

          <div className="space-y-4">
            {formData.details.map((lineItem, index) => (
              <InvoiceLineItemSection
                key={index}
                lineItem={lineItem}
                index={index}
                errors={errors}
                onChange={(field, value) => handleLineItemChange(index, field, value)}
                onRemove={() => handleRemoveLineItem(index)}
                onCalculate={() => handleCalculateLineItem(index)}
              />
            ))}
          </div>
        </div>

        <div className="card-compact p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">Allowances & Charges</h2>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600">Add allowances or charges that apply to the entire invoice</p>
            <button
              onClick={handleAddHeaderAllowanceCharge}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Plus size={16} />
              Add A/C
            </button>
          </div>
          <div className="space-y-2">
            {formData.headerAllowancesCharges.map((ac, index) => (
              <div key={index} className="flex gap-2 items-center">
                <select
                  value={ac.indicator}
                  onChange={(e) => handleHeaderAllowanceChargeChange(index, 'indicator', e.target.value)}
                  className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="A">A - Allowance</option>
                  <option value="C">C - Charge</option>
                </select>
                <select
                  value={ac.code}
                  onChange={(e) => handleHeaderAllowanceChargeChange(index, 'code', e.target.value)}
                  className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="G830">G830 - Shipping and Handling</option>
                  <option value="A470">A470 - Art Work</option>
                  <option value="H170">H170 - Special Tooling Charge</option>
                  <option value="F550">F550 - Premium Charge</option>
                </select>
                <input
                  type="text"
                  value={ac.amount}
                  onChange={(e) => handleHeaderAllowanceChargeChange(index, 'amount', e.target.value)}
                  placeholder="Amount"
                  className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <button
                  onClick={() => handleRemoveHeaderAllowanceCharge(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Plus className="w-5 h-5 rotate-45" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <InvoiceSummarySection summary={formData.summary} />

        {showJsonPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h3 className="text-base font-semibold">JSON Preview</h3>
                <button
                  onClick={() => setShowJsonPreview(false)}
                  className="text-gray-500 hover:text-gray-700 font-bold text-xl"
                >
                  ×
                </button>
              </div>
              <div className="p-4 overflow-auto flex-1">
                <pre className="text-xs bg-gray-50 p-3 rounded border border-gray-200 overflow-auto">
                  {jsonPreview}
                </pre>
              </div>
              <div className="px-4 py-3 border-t flex gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(jsonPreview);
                  }}
                  className="btn-primary px-3 py-1.5 text-sm"
                >
                  Copy
                </button>
                <button
                  onClick={() => setShowJsonPreview(false)}
                  className="btn-secondary px-3 py-1.5 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showExitConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <AlertCircle className="text-orange-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Unsaved Changes
                    </h3>
                    <p className="text-sm text-gray-600">
                      You have unsaved changes that will be lost if you continue. What would you like to do?
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  onClick={() => setShowExitConfirm(false)}
                  className="btn-secondary px-4 py-2 text-sm order-3 sm:order-1"
                >
                  Stay on Page
                </button>
                <button
                  onClick={handleExitWithoutSaving}
                  className="btn-secondary px-4 py-2 text-sm order-2 sm:order-2 text-red-600 hover:bg-red-50"
                >
                  Exit Without Saving
                </button>
                <button
                  onClick={handleSaveAndExitConfirm}
                  className="btn-primary px-4 py-2 text-sm order-1 sm:order-3"
                  disabled={sessionExpired}
                >
                  Save and Exit
                </button>
              </div>
            </div>
          </div>
        )}

        <KeyboardShortcuts isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
      </div>
    </div>
  );
}
